import { useEffect } from 'react';
import { useQuery, UseQueryResult } from '@tanstack/react-query';
import { useDataProvider, useNotify } from 'react-admin';
import type { CrmDataProvider } from '@/providers/types';
import type {
    CommissionsScope,
    CommissionsWidgetResponse,
    ActiveProjectsWidgetResponse,
    QuotationsWidgetResponse,
    OpportunityRangeWidgetResponse,
    PeriodKey,
} from './types';

type WidgetDataMap = {
    commissions: CommissionsWidgetResponse;
    activeProjects: ActiveProjectsWidgetResponse;
    quotations: QuotationsWidgetResponse;
    quotesPendingFinish: QuotationsWidgetResponse;
    opportunityRange: OpportunityRangeWidgetResponse;
};

// Overload signatures for better type inference
export function useWidgetData(
    key: 'commissions',
    period: PeriodKey,
    enabled: boolean,
    extra?: { scope?: CommissionsScope }
): UseQueryResult<CommissionsWidgetResponse, Error>;

export function useWidgetData(
    key: 'activeProjects',
    period: PeriodKey,
    enabled: boolean,
    extra?: { contractorId?: number; scope?: CommissionsScope }
): UseQueryResult<ActiveProjectsWidgetResponse, Error>;

// Quotations and quotesPendingFinish do not depend on the global period toggle.
export function useWidgetData(
    key: 'quotations',
    enabled: boolean,
): UseQueryResult<QuotationsWidgetResponse, Error>;

export function useWidgetData(
    key: 'quotesPendingFinish',
    enabled: boolean,
): UseQueryResult<QuotationsWidgetResponse, Error>;

export function useWidgetData(
    key: 'opportunityRange',
    period: PeriodKey,
    enabled: boolean,
): UseQueryResult<OpportunityRangeWidgetResponse, Error>;

// Implementation
export function useWidgetData<K extends keyof WidgetDataMap>(
    key: K,
    periodOrEnabled: PeriodKey | boolean,
    enabledArg?: boolean,
    extra?: { contractorId?: number; scope?: CommissionsScope }
): UseQueryResult<WidgetDataMap[K], Error> {
    const notify = useNotify();
    const dataProvider = useDataProvider<CrmDataProvider>();

    const noPeriod = key === 'quotations' || key === 'quotesPendingFinish';
    const period = noPeriod ? undefined : (periodOrEnabled as PeriodKey);
    const enabled = noPeriod ? (periodOrEnabled as boolean) : (enabledArg as boolean);

    const fetchFn = () => {
        const metrics = <T,>(path: string, params?: Record<string, string>) =>
            dataProvider.getMetrics(path, params) as Promise<T>;
        switch (key) {
            case 'commissions':
                return metrics<WidgetDataMap[K]>('/commissions', {
                    period: period as PeriodKey,
                    scope: extra?.scope ?? 'all',
                });
            case 'activeProjects': {
                const params: Record<string, string> = {
                    period: period as PeriodKey,
                    scope: extra?.scope ?? 'all',
                };
                if (extra?.contractorId !== undefined) {
                    params.contractor_id = String(extra.contractorId);
                }
                return metrics<WidgetDataMap[K]>('/active-projects', params);
            }
            case 'quotations':
                return metrics<WidgetDataMap[K]>('/quotations');
            case 'quotesPendingFinish':
                return metrics<WidgetDataMap[K]>('/quotes-pending-finish');
            case 'opportunityRange':
                return metrics<WidgetDataMap[K]>('/opportunity-range', {
                    period: period as PeriodKey,
                });
            default:
                throw new Error(`Unknown widget key: ${key}`);
        }
    };

    const result = useQuery<WidgetDataMap[K], Error>({
        queryKey: noPeriod
            ? ['dashboard-widget', key]
            : ['dashboard-widget', key, period, extra?.contractorId, extra?.scope],
        queryFn: fetchFn,
        enabled,
    });

    // Notify after render: calling notify() while rendering updates NotificationContextProvider's state.
    const errorMessage = result.error?.message;
    useEffect(() => {
        if (errorMessage !== undefined) {
            notify(`Failed to load ${key} widget: ${errorMessage}`, {
                type: 'error',
            });
        }
    }, [errorMessage, key, notify]);

    return result as UseQueryResult<WidgetDataMap[K], Error>;
}
